"use client"

import { useQuery } from "@tanstack/react-query"
import { LoadingSpinner } from "@tokenterminal/core/ui/progress/LoadingSpinner"
import {
  Cell,
  HeaderCell,
  Row,
  Table,
  TableBody,
  TableHeader,
} from "@tokenterminal/core/ui/table/Table"
import { formatters } from "@tokenterminal/core/utils/sharedFormatter"
import { trpcClient } from "@tokenterminal/core/utils/trpc"
import { groupBy, uniqBy } from "lodash"
import Image from "next/image"
import { Fragment } from "react"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

interface FinancialStatementWrapperProps {
  projectIds: string[]
  selectedProjectId: string
}

const SECTIONS = ["GMV", "Income statement", "Treasury", "Market data"]

// Skeleton component that matches the table structure
function FinancialStatementSkeleton({ showChanges = true }: { showChanges?: boolean }) {
  // Placeholder text for first column (letters)
  const placeholderLabels = [
    "Trading volume",
    "Transaction volume",
    "Transfer volume",
    "Notional trading volume",
  ]

  // Section headers
  const sectionHeaders = ["GMV", "Income statement", "Market data"]

  // Month headers
  const monthHeaders = ["Jan 2024", "Feb 2024", "Mar 2024", "Apr 2024", "May 2024", "Jun 2024"]

  // Generate placeholder numbers for month columns
  const generatePlaceholderNumber = (rowIndex: number, colIndex: number) => {
    const base = (rowIndex + 1) * (colIndex + 1) * 1234
    return `$${(base / 1000).toFixed(1)}M`
  }

  return (
    <div className="w-full overflow-x-auto">
      <Table
        rowStyle="no-border"
        rowBorderRadius="none"
        className="financial-statement-table w-full"
        density="large"
        style={
          {
            "--cell-font-size": "12px",
            "--cell-padding-x": "8px",
          } as React.CSSProperties
        }
      >
        <TableHeader>
          <Row>
            <HeaderCell className="min-w-[240px] sticky left-0 z-10 bg-bg-surface border-b border-solid border-border-default">
              <div className="flex items-center gap-2">
                <LoadingSpinner diameter={24} />
                <span className="text-base text-fg-default">Calculating statement</span>
              </div>
            </HeaderCell>
            {monthHeaders.map((month, i) => (
              <HeaderCell
                key={i}
                className="border-b border-solid border-border-default"
                style={{
                  minWidth: showChanges ? "140px" : "120px",
                }}
              >
                <div className="flex flex-col gap-0 leading-none items-end">
                  <div className="text-sm font-medium" style={{ filter: "blur(2px)" }}>
                    {month}
                  </div>
                </div>
              </HeaderCell>
            ))}
          </Row>
        </TableHeader>
        <TableBody>
          {sectionHeaders.map((sectionHeader, sectionIndex) => (
            <Fragment key={sectionIndex}>
              {sectionIndex > 0 && (
                <Row>
                  <Cell className="px-0 pt-3.5 pb-1 sticky left-0 z-10 bg-bg-surface">
                    <div className="h-px border-t border-solid border-border-default" />
                  </Cell>
                  <Cell className="px-0 pt-3.5 pb-1" colSpan={6}>
                    <div className="h-px border-t border-solid border-border-default" />
                  </Cell>
                </Row>
              )}
              <Row>
                <Cell className="font-medium pt-3.5 pb-1 sticky left-0 z-10 bg-bg-surface">
                  <div className="text-fg-default" style={{ filter: "blur(2px)" }}>
                    {sectionHeader}
                  </div>
                </Cell>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Cell key={i} className="pt-0 pb-1" />
                ))}
              </Row>
              {Array.from({ length: 4 }).map((_, rowIndex) => (
                <Row key={rowIndex}>
                  <Cell className="py-1! align-top sticky left-0 z-10 bg-bg-surface">
                    <div className="text-fg-secondary" style={{ filter: "blur(2px)" }}>
                      {placeholderLabels[rowIndex % placeholderLabels.length]}
                    </div>
                  </Cell>
                  {Array.from({ length: 6 }).map((_, colIndex) => (
                    <Cell key={colIndex} className="py-1! align-top">
                      <div
                        className="flex items-center justify-end gap-0.5 text-right whitespace-nowrap"
                        style={{
                          fontFamily: "var(--font-geist-mono)",
                          filter: "blur(2px)",
                        }}
                      >
                        <span className="text-fg-secondary/80">$</span>
                        <span className="text-fg-secondary">
                          {generatePlaceholderNumber(rowIndex, colIndex).replace("$", "")}
                        </span>
                      </div>
                    </Cell>
                  ))}
                </Row>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

// Metrics ordered by section (matching the original implementation)
const METRICS_BY_SECTION: { [key: string]: string[] } = {
  GMV: [
    "tvl",
    "active_loans",
    "assets_staked",
    "capital_deployed",
    "outstanding_supply",
    "trading_volume",
    "transaction_volume",
    "transfer_volume",
    "notional_trading_volume",
  ],
  "Income statement": [
    "fees",
    "fees_supply_side",
    "revenue",
    "expenses",
    "cost_of_revenue",
    "superchain_fees",
    "operating_expenses",
    "token_incentives",
    "gross_profit",
    "earnings",
  ],
  Treasury: ["treasury", "treasury_net"],
  "Market data": [
    "price",
    "token_supply_circulating",
    "market_cap_fully_diluted",
    "market_cap_circulating",
    "token_trading_volume",
    "token_turnover_fully_diluted",
    "token_turnover_circulating",
    "tokenholders",
  ],
  "Valuation multiples": [
    "pf_fully_diluted",
    "pf_circulating",
    "ps_fully_diluted",
    "ps_circulating",
  ],
  "Alternative KPIs": [
    "user_dau",
    "user_wau",
    "user_mau",
    "active_addresses_daily",
    "gas_used",
    "transaction_count_contracts",
    "transaction_fee_average",
    "transaction_fee_median",
    "active_developers",
    "code_commits",
  ],
}

const metricsWithParenthesis = new Set([
  "fees_supply_side",
  "expenses",
  "operating_expenses",
  "token_incentives",
  "cost_of_revenue",
  "superchain_fees",
])

const metricsWithIndent = [
  "cost_of_revenue",
  "superchain_fees",
  "operating_expenses",
  "token_incentives",
]

function shouldMetricBeParenthesized(metricId: string): boolean {
  return metricsWithParenthesis.has(metricId)
}

function getMetricLabel(
  metricConfig: { metric_id: string; title: string; short_title?: string },
  project?: { symbol?: string },
): string {
  if (metricConfig.metric_id === "token_trading_volume") {
    return `${project?.symbol ? project.symbol : "Token"} trading volume`
  }

  if (metricConfig.metric_id === "tokenholders") {
    if (!project?.symbol) {
      return metricConfig.title
    }
    return `${project.symbol} ${metricConfig.title.toLowerCase()}`
  }

  return metricConfig.title
}

/**
 * Component that fetches and displays financial statements for multiple projects.
 * Renders all statements and uses opacity to switch between them.
 */
export function FinancialStatementWrapper({
  projectIds,
  selectedProjectId,
}: FinancialStatementWrapperProps) {
  const showDefinitions = false
  const showChanges = true

  // Fetch metrics configuration
  const { data: metricsData } = useQuery({
    queryKey: ["metrics-configuration"],
    queryFn: async () => {
      return await trpcClient.metrics.getMetricsConfiguration.query()
    },
  })

  const metrics = metricsData?.data || []

  // Fetch all project data - call hooks at top level
  const solanaProject = useQuery({
    queryKey: ["project", "solana"],
    queryFn: async () => {
      return await trpcClient.projects.getProject.query({ slug: "solana" })
    },
    enabled: projectIds.includes("solana"),
  })

  const uniswapProject = useQuery({
    queryKey: ["project", "uniswap"],
    queryFn: async () => {
      return await trpcClient.projects.getProject.query({ slug: "uniswap" })
    },
    enabled: projectIds.includes("uniswap"),
  })

  const aaveProject = useQuery({
    queryKey: ["project", "aave"],
    queryFn: async () => {
      return await trpcClient.projects.getProject.query({ slug: "aave" })
    },
    enabled: projectIds.includes("aave"),
  })

  const hyperliquidProject = useQuery({
    queryKey: ["project", "hyperliquid"],
    queryFn: async () => {
      return await trpcClient.projects.getProject.query({
        slug: "hyperliquid",
      })
    },
    enabled: projectIds.includes("hyperliquid"),
  })

  // Helper to get granularity for a project
  const getGranularity = (projectData: typeof solanaProject.data) => {
    const options = projectData?.financial_statement_granularity_options || []
    if (options.length === 0) return null
    if (options.includes("month")) return "month"
    const preferredOrder = ["week", "quarter", "year"]
    const found = preferredOrder.find((option) => options.includes(option))
    return found || options[0]
  }

  // Fetch financial statements for all projects
  const solanaGranularity = getGranularity(solanaProject.data)
  const solanaFinancial = useQuery({
    queryKey: ["financial-statement", "solana", solanaGranularity],
    queryFn: async () => {
      if (!solanaGranularity) return null
      const data = await trpcClient.projects.getFinancialStatement.query({
        project_slug: "solana",
        granularity: solanaGranularity as "year" | "quarter" | "month" | "week",
      })
      return data?.sort(
        (a: { timestamp: string }, b: { timestamp: string }) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
    },
    enabled: !!solanaGranularity && projectIds.includes("solana"),
  })

  const uniswapGranularity = getGranularity(uniswapProject.data)
  const uniswapFinancial = useQuery({
    queryKey: ["financial-statement", "uniswap", uniswapGranularity],
    queryFn: async () => {
      if (!uniswapGranularity) return null
      const data = await trpcClient.projects.getFinancialStatement.query({
        project_slug: "uniswap",
        granularity: uniswapGranularity as "year" | "quarter" | "month" | "week",
      })
      return data?.sort(
        (a: { timestamp: string }, b: { timestamp: string }) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
    },
    enabled: !!uniswapGranularity && projectIds.includes("uniswap"),
  })

  const aaveGranularity = getGranularity(aaveProject.data)
  const aaveFinancial = useQuery({
    queryKey: ["financial-statement", "aave", aaveGranularity],
    queryFn: async () => {
      if (!aaveGranularity) return null
      const data = await trpcClient.projects.getFinancialStatement.query({
        project_slug: "aave",
        granularity: aaveGranularity as "year" | "quarter" | "month" | "week",
      })
      return data?.sort(
        (a: { timestamp: string }, b: { timestamp: string }) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
    },
    enabled: !!aaveGranularity && projectIds.includes("aave"),
  })

  const hyperliquidGranularity = getGranularity(hyperliquidProject.data)
  const hyperliquidFinancial = useQuery({
    queryKey: ["financial-statement", "hyperliquid", hyperliquidGranularity],
    queryFn: async () => {
      if (!hyperliquidGranularity) return null
      const data = await trpcClient.projects.getFinancialStatement.query({
        project_slug: "hyperliquid",
        granularity: hyperliquidGranularity as "year" | "quarter" | "month" | "week",
      })
      return data?.sort(
        (a: { timestamp: string }, b: { timestamp: string }) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
    },
    enabled: !!hyperliquidGranularity && projectIds.includes("hyperliquid"),
  })

  // Combine all project queries
  const projectQueries = [
    {
      projectId: "solana",
      projectData: solanaProject.data,
      isLoadingProject: solanaProject.isLoading,
      financialStatementData: solanaFinancial.data,
      isLoadingFinancial: solanaFinancial.isLoading,
    },
    {
      projectId: "uniswap",
      projectData: uniswapProject.data,
      isLoadingProject: uniswapProject.isLoading,
      financialStatementData: uniswapFinancial.data,
      isLoadingFinancial: uniswapFinancial.isLoading,
    },
    {
      projectId: "aave",
      projectData: aaveProject.data,
      isLoadingProject: aaveProject.isLoading,
      financialStatementData: aaveFinancial.data,
      isLoadingFinancial: aaveFinancial.isLoading,
    },
    {
      projectId: "hyperliquid",
      projectData: hyperliquidProject.data,
      isLoadingProject: hyperliquidProject.isLoading,
      financialStatementData: hyperliquidFinancial.data,
      isLoadingFinancial: hyperliquidFinancial.isLoading,
    },
  ]

  // Check if any project or financial statement is loading
  const isLoadingAny = projectQueries.some((q) => q.isLoadingProject || q.isLoadingFinancial)

  const formatValue = (value: number | null, metricId: string, format?: string) => {
    if (value === null) return "-"

    const metricConfig = metrics.find((m: { metric_id: string }) => m.metric_id === metricId)

    if (format === "percentage" || metricConfig?.format === "percentage") {
      const absFraction = Math.abs(value)
      const colorClassName = value > 0 ? "text-fg-emerald" : "text-fg-red"
      return <span className={colorClassName}>{formatters.percent({ value: absFraction })}</span>
    }

    if (format === "ratio" || metricConfig?.format === "ratio") {
      const ratioText = formatters.ratio({ value: Number(value) })
      return (
        <div className="inline-block">
          {ratioText.replace("x", "")}
          <span className="text-fg-secondary ml-0.5">x</span>
        </div>
      )
    }

    if (format === "number" || metricConfig?.format === "number") {
      return (
        <span>
          {formatters.number({
            value: Number(value),
            abbreviationFormat: "compact",
          })}
        </span>
      )
    }

    // Default to currency format
    return (
      <div className="flex items-center gap-0.5 whitespace-nowrap">
        <span className="text-fg-secondary/80">$</span>
        <span>
          {formatters.number({
            value: Number(value),
            abbreviationFormat: "compact",
          })}
        </span>
      </div>
    )
  }

  // Helper function to render table content
  const renderTable = (data: any, project: any, _isPrevious = false) => {
    if (!data || data.length === 0) return null

    const dataBySection = groupBy(data, "section")
    const uniqueData = uniqBy(data, "timestamp")
    const allTimestamps = (uniqueData as unknown as Array<{ timestamp: string }>)
      .map((d) => d.timestamp)
      .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime())
      .slice(0, 6)

    const columnHeadings = allTimestamps.map((timestamp: string) => {
      const item = data?.find((i: { timestamp: string }) => i.timestamp === timestamp)
      return {
        title: item?.timestamp_label_heading || "",
        subtitle: item?.timestamp_label_subheading || "",
      }
    })

    return (
      <div className="w-full overflow-x-auto max-[1200px]:overflow-x-scroll pt-3 px-3 pb-3 relative">
        <Table
          rowStyle="no-border"
          rowBorderRadius="none"
          className="financial-statement-table w-full"
          density="large"
          style={
            {
              "--cell-font-size": "12px",
              "--cell-padding-x": "8px",
            } as React.CSSProperties
          }
        >
          <TableHeader>
            <Row>
              <HeaderCell className="min-w-[240px] sticky left-0 z-10 bg-bg-surface border-b border-solid border-border-default">
                {project && (
                  <div className="flex items-center gap-2">
                    {project.logo && (
                      <Image
                        src={new URL(project.logo, "https://static1.tokenterminal.com/").href}
                        alt={project.name || ""}
                        width={24}
                        height={24}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    )}
                    <span className="text-base text-fg-default">{project.name}</span>
                  </div>
                )}
              </HeaderCell>
              {showDefinitions && (
                <HeaderCell className="min-w-[390px] sticky left-[240px] z-10 bg-bg-surface border-b border-solid border-border-default">
                  Definition
                </HeaderCell>
              )}
              {columnHeadings.map((heading) => (
                <HeaderCell
                  key={heading.title}
                  className="border-b border-solid border-border-default"
                  style={{
                    minWidth: showChanges ? "140px" : "120px",
                  }}
                >
                  <div className="flex flex-col gap-0 leading-none items-end">
                    <div className="text-sm font-medium">{heading.title}</div>
                  </div>
                </HeaderCell>
              ))}
            </Row>
          </TableHeader>
          <TableBody>
            {SECTIONS.map((section, sectionIndex) => {
              const sectionData = dataBySection[section] || []
              const groupedData = groupBy(sectionData, "metric_id")

              const projectMetrics =
                METRICS_BY_SECTION[section]?.filter(
                  (metricId) => typeof project?.metric_availability?.[metricId] === "boolean",
                ) || []

              if (projectMetrics.length === 0) {
                return null
              }

              return (
                <Fragment key={section}>
                  {sectionIndex > 0 && (
                    <Row className="hidden first-of-type:table-row">
                      <Cell className="pt-3.5 d pb-0 sticky left-0 z-10 bg-bg-surface" />
                      {showDefinitions && (
                        <Cell className="pt-3.5 pb-0 sticky left-[240px] z-10 bg-bg-surface" />
                      )}
                      <Cell className="pt-3.5 pb-0" colSpan={columnHeadings.length} />
                    </Row>
                  )}

                  <Row>
                    <Cell className="font-medium pt-3.5 pb-1 sticky left-0 z-10 bg-bg-surface">
                      {section}
                    </Cell>
                    {showDefinitions && (
                      <Cell className="pt-0 pb-1 sticky left-[240px] z-10 bg-bg-surface w-[200px]">
                        Definition
                      </Cell>
                    )}
                    {columnHeadings.map((heading) => (
                      <Cell key={heading.title} className="pt-0 pb-1" />
                    ))}
                  </Row>

                  {projectMetrics.map((metricId) => {
                    const items = groupedData[metricId] || []
                    const metricConfig = metrics.find(
                      (m: { metric_id: string }) => m.metric_id === metricId,
                    )

                    if (!metricConfig) {
                      return null
                    }

                    const metricTitle = shouldMetricBeParenthesized(metricId)
                      ? `(${getMetricLabel(metricConfig, project)})`
                      : getMetricLabel(metricConfig, project)

                    const dataForTimestamps = allTimestamps.map((timestamp) => {
                      return (
                        items.find((i) => i.timestamp === timestamp) || {
                          value: null,
                          pop_change: null,
                        }
                      )
                    })

                    const isEarnings = metricId === "earnings"
                    const shouldIndent = metricsWithIndent.includes(metricId)

                    return (
                      <Fragment key={metricId}>
                        {isEarnings && (
                          <Row>
                            <Cell className="px-0 pt-1 pb-1 sticky left-0 z-10 bg-bg-surface">
                              <div className="h-px border-t border-solid border-border-default" />
                            </Cell>
                            {showDefinitions && (
                              <Cell className="px-0 pt-1 pb-1 sticky left-[240px] z-10 bg-bg-surface">
                                <div className="h-px border-t border-solid border-border-default" />
                              </Cell>
                            )}
                            <Cell className="px-0 pt-1 pb-1" colSpan={columnHeadings.length}>
                              <div className="h-px border-t border-solid border-border-default" />
                            </Cell>
                          </Row>
                        )}
                        <Row className="group/metric-row">
                          <Cell className="py-1! align-top sticky left-0 z-10 bg-bg-surface group-hover/metric-row:bg-(--row-hover-color)">
                            <div
                              className={`${
                                isEarnings ? "text-fg-default" : "text-fg-secondary"
                              } ${shouldIndent ? "ml-3" : ""}`}
                            >
                              {metricTitle}
                            </div>
                          </Cell>
                          {showDefinitions && (
                            <Cell className="py-1! align-top sticky left-[240px] z-10 bg-bg-surface group-hover/metric-row:bg-(--row-hover-color)">
                              <p className="text-fg-secondary">
                                {items[0]?.definition || metricConfig.definition || ""}
                              </p>
                            </Cell>
                          )}
                          {dataForTimestamps.map((item, i) => {
                            const value = item.value ?? null
                            const popChange = item.pop_change ?? null

                            return (
                              <Cell
                                key={allTimestamps[i] || i}
                                className="py-1! align-top group-hover/metric-row:bg-(--row-hover-color)"
                              >
                                <div
                                  className="flex items-center justify-end gap-px text-right whitespace-nowrap"
                                  style={{
                                    fontFamily: "var(--font-geist-mono)",
                                  }}
                                >
                                  {formatValue(value, metricId, metricConfig.format)}
                                  {showChanges && popChange !== null && (
                                    <div className="min-w-[40px] ml-1 whitespace-nowrap">
                                      {formatValue(popChange, metricId, "percentage")}
                                    </div>
                                  )}
                                </div>
                              </Cell>
                            )
                          })}
                        </Row>
                      </Fragment>
                    )
                  })}

                  {sectionIndex < SECTIONS.length - 1 && (
                    <Row>
                      <Cell className="px-0 pt-3.5 pb-1 sticky left-0 z-10 bg-bg-surface">
                        <div className="h-px border-t border-solid border-border-default" />
                      </Cell>
                      {showDefinitions && (
                        <Cell className="px-0 pt-3.5 pb-1 sticky left-[240px] z-10 bg-bg-surface">
                          <div className="h-px border-t border-solid border-border-default" />
                        </Cell>
                      )}
                      <Cell className="px-0 pt-3.5 pb-1" colSpan={columnHeadings.length}>
                        <div className="h-px border-t border-solid border-border-default" />
                      </Cell>
                    </Row>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </div>
    )
  }

  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-2xl"
      style={{
        maxHeight: "500px",
        overflow: "hidden",
        minHeight: "500px",
        ...FADE_MASK_STYLE,
      }}
    >
      {isLoadingAny ? (
        <div className="overflow-x-hidden overflow-y-hidden">
          <FinancialStatementSkeleton showChanges={showChanges} />
        </div>
      ) : (
        <div className="overflow-x-hidden overflow-y-hidden relative w-full">
          {projectQueries.map((query) => {
            const isVisible = query.projectId === selectedProjectId
            const hasData = query.financialStatementData && query.financialStatementData.length > 0

            if (!hasData) return null

            return (
              <div
                key={query.projectId}
                className={`w-full ${
                  isVisible ? "opacity-100 relative" : "opacity-0 absolute inset-0"
                }`}
                style={{
                  pointerEvents: isVisible ? "auto" : "none",
                  zIndex: isVisible ? 1 : 0,
                }}
              >
                {renderTable(query.financialStatementData, query.projectData, false)}
              </div>
            )
          })}
          {projectQueries.every(
            (q) => !q.financialStatementData || q.financialStatementData.length === 0,
          ) && (
            <div className="flex items-center justify-center p-8">
              <div className="text-sm text-fg-secondary">
                No financial statement data available.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
